import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const Team = () => {
  const executives = [
    {
      name: 'Nabeel',
      title: 'Chief Executive Officer',
      bio: 'With a PhD in Computer Science, Nabeel brings over 18 years of experience in AI and machine learning, previously leading innovation at Meta, Autodesk, Intel and Walt Disney. He founded AlfasTech with a vision to make advanced AI accessible to businesses of all sizes.',
      image: 'https://www.cs.purdue.edu/cgvlab/www/resources/images/team/butt_nabeel.jpg'
    },
    {
      name: 'Ali',
      title: 'Chief Technology Officer',
      bio: 'Ali is a visionary technology leader with over 8 years of experience driving innovation in AI agent development, cloud-native architectures, and enterprise-scale software solutions. As a strategic architect of autonomous systems, he has pioneered the integration of cutting-edge tools to build self-optimizing AI agents that redefine customer engagement.',
      image: '/assets/team/Ali.png'
    },
    {
      name: 'Danish',
      title: 'Chief Operations Officer',
      bio: "Danish is a seasoned COO with a strong track record of leading companies and managing global client collaborations across North America, Europe, and Australia. At AlfasTech, Danish oversees operations with a focus on efficiency and international growth.",
      image: '/assets/team/Danish.png'
    }
  ]

  const team_members = [
    { role: "Tech Leads", department: "Engineering Leadership", count: 2 },
    { role: "Software Engineers", department: "Engineering", count: 6 },
    { role: "UI/UX Designer", department: "Design", count: 1 },
    { role: "ML Engineer", department: "AI/ML", count: 2 },
    { role: "Data Scientist", department: "AI/ML", count: 2 },
    { role: "Data Security Engineer", department: "AI/ML", count: 1 },
    { role: "DevOps Engineer", department: "Infrastructure", count: 1 },
    { role: "QA Engineer", department: "Quality Assurance", count: 2 },
    { role: "Product Manager", department: "Product", count: 1 },
    { role: "Business Analyst", department: "Strategy", count: 1 },
  ]

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-background">
            {/* Grid pattern */}
            <div className="absolute inset-0 opacity-20">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(#5F72FF22 1px, transparent 1px), linear-gradient(90deg, #5F72FF22 1px, transparent 1px)",
                  backgroundSize: "40px 40px",
                }}
              ></div>
            </div>

            {/* Animated gradient blobs */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0.3 }}
              animate={{
                scale: [0.8, 1.2, 0.9, 1.1, 0.8],
                opacity: [0.3, 0.4, 0.2, 0.5, 0.3],
                x: ["-10%", "5%", "0%", "-5%", "-10%"],
                y: ["5%", "-5%", "10%", "0%", "5%"],
              }}
              transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY, repeatType: "loop" }}
              className="absolute top-1/4 left-1/4 w-[40vw] h-[40vw] rounded-full bg-accent opacity-20 blur-[100px]"
            />
          </div>
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6 font-futuristic"
            >
              Who We Are
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white mb-8"
            >
              Meet the team behind AlfasTech's innovative AI solutions. Our leadership combines expertise in artificial
              intelligence, business operations, and customer service to deliver transformative solutions.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Executive Team Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-white mb-16 text-center font-futuristic"
          >
            Leadership Team
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {executives.map((executive, index) => (
              <motion.div
                key={executive.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="backdrop-blur-xl bg-white/10 rounded-2xl overflow-hidden shadow-xl border border-white/20 hover:border-accent/70 transition-all duration-300 cursor-pointer group"
              >
                <Link to={`/${executive.name.toLowerCase().replace(' ', '-')}`} className="block">
                  <div className="h-80 overflow-hidden relative">
                    <img
                      src={executive.image || "/placeholder.svg"}
                      alt={executive.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-accent bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                      <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-accent bg-opacity-70 px-4 py-2 rounded-full">
                        View Profile
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-1 group-hover:text-accent transition-colors duration-300">
                      {executive.name}
                    </h3>
                    <p className="text-accent mb-4">{executive.title}</p>
                    <p className="text-white">{executive.bio}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Structure Section */}
      <section className="py-20 bg-background/50">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl font-bold text-white mb-16 text-center font-futuristic"
          >
            Team Structure
          </motion.h2>

          <div className="overflow-x-auto backdrop-blur-xl bg-white/10 rounded-2xl border-2 border-white/20 p-4">
            <table className="min-w-full text-left text-sm border-collapse">
              <thead className="bg-accent/20 text-white uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4 rounded-tl-lg">Role</th>
                  <th className="py-3 px-4">Department</th>
                  <th className="py-3 px-4 rounded-tr-lg">Count</th>
                </tr>
              </thead>
              <tbody className="text-white">
                {team_members.map((member, index) => (
                  <tr key={index} className="border-t border-white/10 hover:bg-white/5 transition-colors">
                    <td className="py-3 px-4">{member.role}</td>
                    <td className="py-3 px-4">{member.department}</td>
                    <td className="py-3 px-4">{member.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Company Values Section */}
      <section className="py-20 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold text-white mb-4 font-futuristic"
            >
              Our Values
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white max-w-3xl mx-auto"
            >
              The principles that guide everything we do at AlfasTech
            </motion.p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Innovation",
                description:
                  "We constantly push the boundaries of what's possible with AI, developing cutting-edge solutions that solve real business problems.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                ),
              },
              {
                title: "Customer Success",
                description:
                  "We measure our success by the success of our customers. Their goals become our goals, and we're committed to helping them achieve exceptional results.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                    />
                  </svg>
                ),
              },
              {
                title: "Ethical AI",
                description:
                  "We develop AI responsibly, with a commitment to fairness, transparency, and privacy. We believe AI should augment human capabilities, not replace them.",
                icon: (
                  <svg
                    className="w-12 h-12 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                ),
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="backdrop-blur-xl bg-white/10 p-8 rounded-2xl border-2 border-white/20 hover:border-accent/70 transition-all duration-300 group shadow-xl"
              >
                <div className="mb-6 text-white group-hover:text-highlight transition-colors duration-300">
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-gradient-to-br from-accent to-accent2 border-2 border-white/50 group-hover:border-highlight/50 transition-all duration-300 shadow-lg shadow-accent/30">
                    {value.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-3 text-white group-hover:text-highlight transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-white group-hover:text-white transition-colors duration-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Our Team CTA */}
      <section className="py-20 bg-gradient-to-br from-accent/30 to-accent2/30 relative overflow-hidden">
        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-accent opacity-20 blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full bg-accent2 opacity-20 blur-3xl animate-pulse-slower"></div>
        
        <div className="container-custom relative z-10">
          <div className="backdrop-blur-lg bg-background/80 border border-white/20 rounded-3xl p-12 md:p-16 max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl font-bold mb-6 text-white text-center font-futuristic"
            >
              Join Our Team
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl mb-8 text-white text-center"
            >
              We're always looking for talented individuals who are passionate about AI and customer service. Explore our
              current openings and become part of our innovative team.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex justify-center"
            >
              <Link
                to="/careers"
                className="px-8 py-4 rounded-full font-bold text-lg bg-gradient-to-r from-accent to-accent2 shadow-xl hover:scale-105 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-accent2/40 text-white border-2 border-white/20"
              >
                View Open Positions
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Team
